import React from "react"
import ReactDOM from "react-dom/client"
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,

  useNavigate,
} from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import "./index.css"
import { useAuth } from "./lib/auth"
import { useEmbedBridge, isEmbedded } from "./lib/use-embed"
import { PeopleDashboard } from "./pages/Dashboard"
import { EmployeesPage } from "./pages/Employees"
import { PayrollPage } from "./pages/Payroll"
import { LoginPage } from "./pages/Login"
import { ReportsPage } from "./pages/Reports"
import { AuditLogPage } from "./pages/AuditLog"
import { SettingsPage } from "./pages/Settings"
import { LeavePage } from "./pages/Leave"
import { AttendancePage } from "./pages/Attendance"
import { MyDashboard } from "./pages/MyDashboard"
import { MyPayslips } from "./pages/MyPayslips"
import { MyLeave } from "./pages/MyLeave"
import { MyProfile } from "./pages/MyProfile"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false },
  },
})

const basename = import.meta.env.PROD ? "/people" : "/"

function NavLink(props: { to: string; children: React.ReactNode }) {
  const location = useLocation()
  const active =
    location.pathname === props.to || (props.to !== "/" && location.pathname.startsWith(props.to))
  return (
    <Link
      to={props.to}
      className={`px-3 py-2 rounded text-sm font-medium ${
        active ? "bg-brand-500 text-white" : "text-slate-700 hover:bg-slate-200"
      }`}
    >
      {props.children}
    </Link>
  )
}

function TenantSwitcher() {
  const { memberships, activeSlug, setActiveSlug } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  if (memberships.length <= 1) {
    return activeSlug ? (
      <span className="text-xs font-mono text-slate-500">{activeSlug}</span>
    ) : null
  }

  return (
    <select
      value={activeSlug ?? ""}
      onChange={(e) => {
        const next = e.target.value
        setActiveSlug(next)
        // Replace the slug segment in the URL so we stay on the same page in the new tenant.
        const newPath = location.pathname.replace(/^\/t\/[^/]+/, `/t/${next}`)
        navigate(newPath || `/t/${next}/employees`)
      }}
      className="px-2 py-1 border border-slate-300 rounded text-xs bg-white"
    >
      {memberships.map((m) => (
        <option key={m.tenantSlug} value={m.tenantSlug}>
          {m.legalName}
        </option>
      ))}
    </select>
  )
}

function AuthenticatedShell() {
  const { user, activeSlug, logout } = useAuth()
  const navigate = useNavigate()
  if (!user) return <Navigate to="/login" replace />

  const tenantBase = activeSlug ? `/t/${activeSlug}` : ""

  return (
    <div className="min-h-screen flex flex-col">
      {!isEmbedded() && <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-6">
          <div className="font-semibold text-brand-700">ClearAI People</div>
          <nav className="flex gap-1">
            <NavLink to={`${tenantBase}/dashboard`}>Dashboard</NavLink>
            <NavLink to={`${tenantBase}/employees`}>Employees</NavLink>
            <NavLink to={`${tenantBase}/leave`}>Leave</NavLink>
            <NavLink to={`${tenantBase}/attendance`}>Attendance</NavLink>
            <NavLink to={`${tenantBase}/payroll`}>Payroll</NavLink>
            <NavLink to={`${tenantBase}/reports`}>Reports</NavLink>
            <NavLink to={`${tenantBase}/audit`}>Audit</NavLink>
            <NavLink to={`${tenantBase}/settings`}>Settings</NavLink>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <TenantSwitcher />
            <span className="text-xs text-slate-500">{user.fullName}</span>
            <button
              onClick={() => {
                logout()
                navigate("/login", { replace: true })
              }}
              className="text-xs text-slate-500 hover:text-slate-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <Routes>
          <Route
            path="/"
            element={
              activeSlug ? (
                <Navigate to={`/t/${activeSlug}/employees`} replace />
              ) : (
                <NoTenantLanding />
              )
            }
          />
          <Route path="/t/:slug/dashboard" element={<PeopleDashboard />} />
          <Route path="/t/:slug/employees" element={<EmployeesPage />} />
          <Route path="/t/:slug/leave" element={<LeavePage />} />
          <Route path="/t/:slug/attendance" element={<AttendancePage />} />
          <Route path="/t/:slug/payroll" element={<PayrollPage />} />
          <Route path="/t/:slug/reports" element={<ReportsPage />} />
          <Route path="/t/:slug/audit" element={<AuditLogPage />} />
          <Route path="/t/:slug/settings" element={<SettingsPage />} />
          <Route path="*" element={<div className="text-slate-500">Page not found.</div>} />
        </Routes>
      </main>
    </div>
  )
}


function RoleBasedShell() {
  const { memberships, activeSlug } = useAuth()
  // First active membership decides shell. Employees see the limited shell;
  // anyone with hr_admin / tenant_owner / payroll_* / manager / auditor /
  // platform_admin sees the admin shell.
  const m = memberships.find((mb) => mb.tenantSlug === activeSlug) ?? memberships[0]
  if (m?.role === "employee") return <EmployeeShell />
  return <AuthenticatedShell />
}

function EmployeeShell() {
  const { user, activeSlug, logout } = useAuth()
  const navigate = useNavigate()
  if (!user) return <Navigate to="/login" replace />

  const tenantBase = activeSlug ? `/t/${activeSlug}` : ""

  return (
    <div className="min-h-screen flex flex-col">
      {!isEmbedded() && <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-6">
          <div className="font-semibold text-brand-700">ClearAI People</div>
          <nav className="flex gap-1">
            <NavLink to={`${tenantBase}/me/dashboard`}>Dashboard</NavLink>
            <NavLink to={`${tenantBase}/me/payslips`}>My payslips</NavLink>
            <NavLink to={`${tenantBase}/me/leave`}>My leave</NavLink>
            <NavLink to={`${tenantBase}/me/profile`}>My profile</NavLink>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">{user.fullName}</span>
            <button
              onClick={() => {
                logout()
                navigate("/login", { replace: true })
              }}
              className="text-xs text-slate-500 hover:text-slate-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6">
        <Routes>
          <Route path="/" element={activeSlug ? <Navigate to={`/t/${activeSlug}/me/dashboard`} replace /> : <NoTenantLanding />} />
          <Route path="/t/:slug/me/dashboard" element={<MyDashboard />} />
          <Route path="/t/:slug/me/payslips" element={<MyPayslips />} />
          <Route path="/t/:slug/me/leave" element={<MyLeave />} />
          <Route path="/t/:slug/me/profile" element={<MyProfile />} />
          <Route path="*" element={<Navigate to={activeSlug ? `/t/${activeSlug}/me/dashboard` : "/"} replace />} />
        </Routes>
      </main>
    </div>
  )
}

function NoTenantLanding() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  return (
    <div className="bg-white border border-slate-200 rounded p-6 max-w-md">
      <div className="font-medium text-slate-900 mb-2">No tenant access yet</div>
      <p className="text-sm text-slate-500 mb-4">
        You're signed in as {user?.email}, but no tenant has been assigned to your account. Ask a
        platform admin to grant you membership, then sign in again.
      </p>
      <button
        onClick={() => {
          logout()
          navigate("/login", { replace: true })
        }}
        className="text-sm text-slate-600 hover:text-slate-900"
      >
        Sign out
      </button>
    </div>
  )
}

function App() {
  useEmbedBridge()
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<RoleBasedShell />} />
    </Routes>
  )
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={basename}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)
